import type React from "react";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import type { Goal } from "@/types/goal";
import { useWeeklyProgress } from "@/hooks/useWeeklyProgress";
import { useMemo } from "react";

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
);

interface WeeklySummaryProps {
	goals: Goal[];
}

const WeeklySummary: React.FC<WeeklySummaryProps> = ({ goals }) => {
	const { weeklyProgress, loading, error } = useWeeklyProgress(
		goals.map((goal) => goal.id),
	);

	const chartData = useMemo(() => {
		if (loading || error || !weeklyProgress.length) {
			return {
				labels: ["月", "火", "水", "木", "金", "土", "日"],
				datasets: [],
			};
		}

		const labels = ["月", "火", "水", "木", "金", "土", "日"];
		const colors = [
			"rgba(255, 99, 132, 0.5)",
			"rgba(54, 162, 235, 0.5)",
			"rgba(255, 206, 86, 0.5)",
			"rgba(75, 192, 192, 0.5)",
			"rgba(153, 102, 255, 0.5)",
		];

		return {
			labels,
			datasets: goals.map((goal, index) => {
				const goalProgress = weeklyProgress.find((wp) => wp.goalId === goal.id);
				return {
					label: goal.title,
					data: goalProgress?.weeklyData || Array(7).fill(0),
					backgroundColor: colors[index % colors.length],
				};
			}),
		};
	}, [goals, weeklyProgress, loading, error]);

	const options = {
		responsive: true,
		plugins: {
			legend: {
				position: "top" as const,
			},
			title: {
				display: true,
				text: "週次進捗サマリー",
			},
		},
	};

	if (loading) return <div>読み込み中...</div>;
	if (error) return <div>エラーが発生しました</div>;

	return <Bar options={options} data={chartData} />;
};

export default WeeklySummary;
